import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fixedHorizonOptionScorecardActivationWarning } from '@einhaus/project314-shared';
import { Button, Card, ConfirmationDialog, FormInput, FormNumberInput, FormSelect, Modal } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { EntryScorecardsRow } from '@/types/appRouter';
import {
    getBaselineExtractionButtonLabel,
    getScorecardInitializationClassName,
    getScorecardInitializationHelp,
    getScorecardInitializationLabel
} from './helpers';
import ScorecardTargetProvenancePanel from './ScorecardTargetProvenancePanel';
import type { ScorecardTargetProvenance, SizingBand, StrategyContext, ThresholdsMeta } from './types';

interface ScorecardMetadataCardProps {
    scorecard: EntryScorecardsRow;
    strategyContext: StrategyContext | null;
    isOwnedScorecard: boolean;
    canEditScorecard: boolean;
    invalidateList: () => void;
    name: string;
    setName: React.Dispatch<React.SetStateAction<string>>;
    targetProvenance: ScorecardTargetProvenance | null;
    targetMetric: 'stock' | 'option' | 'future';
    setTargetMetric: React.Dispatch<React.SetStateAction<'stock' | 'option' | 'future'>>;
    minPredictedProfit: number;
    setMinPredictedProfit: React.Dispatch<React.SetStateAction<number>>;
    thresholds: ThresholdsMeta;
    setThresholds: React.Dispatch<React.SetStateAction<ThresholdsMeta>>;
    vetoLiftThreshold: number | null;
    setVetoLiftThreshold: React.Dispatch<React.SetStateAction<number | null>>;
    sizingPolicy: SizingBand[];
    setSizingPolicy: React.Dispatch<React.SetStateAction<SizingBand[]>>;
    onShare: () => void;
    onRequestDelete: () => void;
    isDeletingScorecard: boolean;
    onSave: () => Promise<boolean>;
    isSaving: boolean;
}

const ScorecardMetadataCard = ({
    scorecard,
    strategyContext,
    isOwnedScorecard,
    canEditScorecard,
    invalidateList,
    name,
    setName,
    targetProvenance,
    targetMetric,
    setTargetMetric,
    minPredictedProfit,
    setMinPredictedProfit,
    thresholds,
    setThresholds,
    vetoLiftThreshold,
    setVetoLiftThreshold,
    sizingPolicy,
    setSizingPolicy,
    onShare,
    onRequestDelete,
    isDeletingScorecard,
    onSave,
    isSaving
}: ScorecardMetadataCardProps) => {
    const strategyIdForContext = strategyContext?.strategyId ?? 0;
    const isLocked = scorecard.lockedAt !== null;
    const [isActivationConfirmOpen, setIsActivationConfirmOpen] = useState(false);
    const [isDuplicateNameOpen, setIsDuplicateNameOpen] = useState(false);
    const [duplicateName, setDuplicateName] = useState('');

    // Shared with the backend attach gate: non-null only for fixed-horizon option scorecards, whose
    // activation needs an explicit acknowledgment. Uses the form's target metric because the unsaved
    // edit is saved right before attaching.
    const activationWarning = fixedHorizonOptionScorecardActivationWarning({ targetMetric, objectiveMode: scorecard.objectiveMode });
    const lockNotice = isLocked
        ? null
        : 'This exact scorecard version will be locked against further edits and attached to this strategy. You can create a new editable version later.';

    const scorecardContextQuery = useQuery({
        ...trpc.backtest.getStrategyScorecardContext.queryOptions({
            strategyId: strategyIdForContext || 0
        }),
        enabled: isOwnedScorecard && !!strategyIdForContext
    });

    useEffect(() => {
        if (scorecardContextQuery.error) {
            handleTrpcQuery(scorecardContextQuery, {
                fallbackErrorMessage: 'Failed to fetch strategy scorecard/backtest context',
                showErrorToast: false
            });
        }
    }, [scorecardContextQuery.error]);

    const attachMutation = useTrpcMutation(trpc.scorecards.attach.mutationOptions({ onSuccess: invalidateList }), {
        successMessage: 'Scorecard version attached for testing',
        fallbackErrorMessage: 'Failed to attach scorecard'
    });

    const detachMutation = useTrpcMutation(trpc.scorecards.detach.mutationOptions({ onSuccess: invalidateList }), {
        successMessage: 'Scorecard detached',
        fallbackErrorMessage: 'Failed to detach scorecard'
    });

    const duplicateMutation = useTrpcMutation(trpc.scorecards.duplicate.mutationOptions({ onSuccess: invalidateList }), {
        successMessage: 'Editable version created',
        fallbackErrorMessage: 'Failed to duplicate scorecard'
    });

    const extractBaselinesMutation = useTrpcMutation(trpc.scorecards.extractBaselines.mutationOptions({ onSuccess: invalidateList }), {
        successMessage: 'Baseline extraction started',
        fallbackErrorMessage: 'Failed to start extraction'
    });

    const attachScorecardVersion = async () => {
        if (!strategyContext) throw new Error('A strategy context is required to attach a scorecard');
        if (!isLocked && !(await onSave())) return;

        await attachMutation.mutateAsync({
            strategyId: strategyContext.strategyId,
            scorecardId: scorecard.id,
            acknowledgeFixedHorizonProxy: activationWarning !== null
        });
    };

    const submitDuplicateScorecardVersion = async () => {
        const trimmedName = duplicateName.trim();
        if (!trimmedName) return;

        const result = await duplicateMutation.mutateAsync({ scorecardId: scorecard.id, name: trimmedName });
        setIsDuplicateNameOpen(false);
        window.location.href = strategyIdForContext
            ? `/user/scorecard/${result.id}?strategyId=${strategyIdForContext}`
            : `/user/scorecard/${result.id}`;
    };

    const addSizingBand = () => setSizingPolicy((prev) => [...prev, { minScore: 0, maxScore: 100, multiplier: 1 }]);

    const updateSizingBand = (i: number, field: keyof SizingBand, value: number) => {
        setSizingPolicy((prev) => prev.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
    };

    const removeSizingBand = (i: number) => setSizingPolicy((prev) => prev.filter((_, idx) => idx !== i));

    return (
        <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-white text-lg font-semibold">Metadata</h3>
                    <span className="rounded-full border border-zinc-600 bg-zinc-700/20 px-2 py-1 text-xs text-zinc-300">
                        {scorecard.initializationStatus}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {isOwnedScorecard && (
                        <Button variant="secondary" onClick={onShare}>
                            Share
                        </Button>
                    )}
                    {isOwnedScorecard && isLocked && (
                        <Button
                            disabled={duplicateMutation.isPending}
                            variant="secondary"
                            onClick={() => {
                                setDuplicateName(`${scorecard.name} Revision`);
                                setIsDuplicateNameOpen(true);
                            }}
                        >
                            {duplicateMutation.isPending ? 'Creating...' : 'New Editable Version'}
                        </Button>
                    )}
                    {isOwnedScorecard &&
                        strategyContext &&
                        (scorecardContextQuery.data?.activeScorecard?.id === scorecard.id ? (
                            <Button
                                disabled={detachMutation.isPending}
                                variant="secondary"
                                onClick={async () => {
                                    await detachMutation.mutateAsync({ strategyId: strategyContext.strategyId });
                                }}
                            >
                                {detachMutation.isPending ? 'Detaching...' : 'Detach'}
                            </Button>
                        ) : (
                            <Button
                                disabled={attachMutation.isPending || isSaving || scorecard.initializationStatus !== 'ready'}
                                variant="primary"
                                onClick={async () => {
                                    if (lockNotice || activationWarning) {
                                        setIsActivationConfirmOpen(true);
                                        return;
                                    }

                                    await attachScorecardVersion();
                                }}
                            >
                                {attachMutation.isPending
                                    ? 'Attaching...'
                                    : scorecard.initializationStatus === 'ready'
                                      ? isLocked
                                          ? 'Attach Version'
                                          : 'Test This Version'
                                      : 'Attach (not ready)'}
                            </Button>
                        ))}
                    {isOwnedScorecard && (
                        <Button disabled={isDeletingScorecard} variant="danger" onClick={onRequestDelete}>
                            Delete Scorecard
                        </Button>
                    )}
                    {canEditScorecard && (
                        <Button disabled={isSaving} variant="primary" onClick={onSave}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    )}
                </div>
            </div>
            {isOwnedScorecard && strategyContext && scorecard.initializationStatus === 'ready' && !isLocked && (
                <div className="mb-4 rounded border border-emerald-700/60 bg-emerald-950/20 p-3 text-sm text-emerald-100">
                    Editable test draft. Adjust minimum matched rules, minimum predicted profit, or rule selectors, then use the symbol
                    preview below. Test This Version locks and attaches these exact settings; it does not run backtests automatically.
                </div>
            )}
            {isOwnedScorecard &&
                strategyContext &&
                isLocked &&
                scorecardContextQuery.data?.activeScorecard?.id === scorecard.id &&
                !scorecardContextQuery.data.hasBacktests && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded border border-amber-700/60 bg-amber-950/20 p-3 text-sm text-amber-100">
                        <span>Attached and locked. Run current-version strategy backtests to evaluate this scorecard.</span>
                        <Button size="sm" to={`/user/tradingStrategy/${strategyContext.strategyId}?section=backtests`} variant="secondary">
                            Open Backtests
                        </Button>
                    </div>
                )}
            {isOwnedScorecard &&
                strategyContext &&
                isLocked &&
                scorecardContextQuery.data?.activeScorecard?.id === scorecard.id &&
                scorecardContextQuery.data.hasBacktests && (
                    <div className="mb-4 rounded border border-blue-700/60 bg-blue-950/20 p-3 text-sm text-blue-100">
                        This locked scorecard has current-version strategy backtests. Review the results before promoting the variant.
                    </div>
                )}
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-zinc-400">
                    Initialization:{' '}
                    <span className={getScorecardInitializationClassName(scorecard.initializationStatus)}>
                        {getScorecardInitializationLabel(scorecard.initializationStatus)}
                    </span>
                    {getScorecardInitializationHelp(scorecard.initializationStatus) && (
                        <span className="text-zinc-500"> — {getScorecardInitializationHelp(scorecard.initializationStatus)}</span>
                    )}
                </div>
                {isOwnedScorecard && (
                    <div className="flex items-center gap-3">
                        {scorecardContextQuery.data?.activeScorecard && scorecardContextQuery.data.activeScorecard.id !== scorecard.id && (
                            <div className="text-xs text-zinc-400">
                                Attaching will replace {scorecardContextQuery.data.activeScorecard.name} on this strategy.
                            </div>
                        )}
                        {scorecard.initializationStatus !== 'ready' && (
                            <Button
                                disabled={extractBaselinesMutation.isPending || scorecard.initializationStatus === 'extracting'}
                                size="sm"
                                variant="secondary"
                                onClick={() => extractBaselinesMutation.mutate({ scorecardId: scorecard.id })}
                            >
                                {getBaselineExtractionButtonLabel(scorecard.initializationStatus, extractBaselinesMutation.isPending)}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {isOwnedScorecard &&
                strategyContext &&
                scorecardContextQuery.data?.activeScorecard?.id === scorecard.id &&
                scorecardContextQuery.data.hasBacktests &&
                scorecardContextQuery.data.hasStaleBacktests && (
                    <div className="mt-3 rounded border border-amber-500/60 bg-amber-500/10 p-3 text-xs text-amber-100">
                        Some existing backtests for this strategy were last fully refreshed before this scorecard&apos;s most recent update.
                        For consistent predictedProfit and scorecard decisions, consider overwriting or repopulating backtests for this
                        strategy from the Backtests section of the strategy page.
                    </div>
                )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput disabled={!canEditScorecard} label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                <FormSelect
                    disabled={!canEditScorecard || scorecard.targetMetric === 'future'}
                    label="Target Metric"
                    options={[
                        { value: 'stock', label: 'Stock Returns' },
                        { value: 'option', label: 'Option Returns' },
                        ...(scorecard.targetMetric === 'future' ? [{ value: 'future', label: 'Native Futures Returns' }] : [])
                    ]}
                    value={targetMetric}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTargetMetric(e.target.value as 'stock' | 'option' | 'future')}
                />
            </div>
            <div className="mt-3">
                <ScorecardTargetProvenancePanel provenance={targetProvenance} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <FormNumberInput
                    disabled={!canEditScorecard}
                    label="Min Predicted Profit (%)"
                    step={0.1}
                    value={minPredictedProfit}
                    onValueChange={(value) => {
                        // Required thresholds keep their stored value while the field is empty.
                        if (value === null) return;

                        setMinPredictedProfit(value);
                    }}
                />
                <FormNumberInput
                    integer
                    disabled={!canEditScorecard}
                    label="Min Matched Rules"
                    min={0}
                    value={thresholds.minMatchedRules}
                    onValueChange={(value) => {
                        if (value === null) return;

                        setThresholds((t) => ({ ...t, minMatchedRules: value }));
                    }}
                />
                <FormNumberInput
                    integer
                    disabled={!canEditScorecard}
                    label="Min Positive Boost Matches"
                    min={0}
                    value={thresholds.minPositiveMatchedRules}
                    onValueChange={(value) => {
                        if (value === null) return;

                        setThresholds((t) => ({ ...t, minPositiveMatchedRules: value }));
                    }}
                />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <FormNumberInput
                    disabled={!canEditScorecard}
                    label="Veto Lift Threshold (%) - Optional"
                    placeholder="e.g. -10 to reject if any lift < -10%"
                    step={0.1}
                    value={vetoLiftThreshold}
                    onValueChange={setVetoLiftThreshold}
                />
                <div className="text-xs text-zinc-400 flex items-center">
                    If set, entries will be rejected when ANY rule has a lift below this threshold
                </div>
            </div>
            <div className="mt-6">
                {scorecard.targetMetric === 'future' ? (
                    <p className="text-sm text-zinc-400">Native futures allocation stays fixed by the execution configuration.</p>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-white font-medium">Sizing Policy</div>
                            <Button disabled={!canEditScorecard} size="sm" variant="secondary" onClick={addSizingBand}>
                                + Add Band
                            </Button>
                        </div>
                        {sizingPolicy.length === 0 ? (
                            <div className="text-zinc-500 text-sm">No bands configured</div>
                        ) : (
                            <div className="space-y-2">
                                {sizingPolicy.map((band, idx) => (
                                    <div className="grid grid-cols-7 gap-2 items-end" key={idx}>
                                        <div className="col-span-2">
                                            <FormNumberInput
                                                disabled={!canEditScorecard}
                                                label="Min Score"
                                                step={0.1}
                                                value={band.minScore}
                                                onValueChange={(value) => {
                                                    if (value === null) return;

                                                    updateSizingBand(idx, 'minScore', value);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <FormNumberInput
                                                disabled={!canEditScorecard}
                                                label="Max Score"
                                                step={0.1}
                                                value={band.maxScore}
                                                onValueChange={(value) => {
                                                    if (value === null) return;

                                                    updateSizingBand(idx, 'maxScore', value);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <FormNumberInput
                                                disabled={!canEditScorecard}
                                                label="Multiplier"
                                                step={0.1}
                                                value={band.multiplier}
                                                onValueChange={(value) => {
                                                    if (value === null) return;

                                                    updateSizingBand(idx, 'multiplier', value);
                                                }}
                                            />
                                        </div>
                                        <div className="col-span-1">
                                            <Button
                                                disabled={!canEditScorecard}
                                                size="sm"
                                                variant="danger"
                                                onClick={() => removeSizingBand(idx)}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <ConfirmationDialog
                confirmText={isLocked ? 'Attach Version' : 'Lock & Attach'}
                confirmVariant="primary"
                isConfirmLoading={attachMutation.isPending || isSaving}
                isOpen={isActivationConfirmOpen}
                message={
                    <div className="space-y-3 text-sm">
                        {lockNotice && <p>{lockNotice}</p>}
                        {activationWarning && (
                            <p className="rounded border border-amber-700/60 bg-amber-950/30 p-3 text-amber-100">{activationWarning}</p>
                        )}
                    </div>
                }
                title={isLocked ? 'Attach this scorecard version?' : 'Test this scorecard version?'}
                onCancel={() => setIsActivationConfirmOpen(false)}
                onConfirm={async () => {
                    try {
                        await attachScorecardVersion();
                    } finally {
                        setIsActivationConfirmOpen(false);
                    }
                }}
            />

            <Modal
                footer={
                    <>
                        <Button disabled={duplicateMutation.isPending} variant="outline-dark" onClick={() => setIsDuplicateNameOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!duplicateName.trim()}
                            isLoading={duplicateMutation.isPending}
                            variant="primary"
                            onClick={submitDuplicateScorecardVersion}
                        >
                            Create Editable Version
                        </Button>
                    </>
                }
                isOpen={isDuplicateNameOpen}
                title="New editable version"
                onClose={() => setIsDuplicateNameOpen(false)}
            >
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void submitDuplicateScorecardVersion();
                    }}
                >
                    <FormInput
                        autoFocus
                        label="Name for the editable version"
                        value={duplicateName}
                        onChange={(e) => setDuplicateName(e.target.value)}
                    />
                </form>
            </Modal>
        </Card>
    );
};

export default ScorecardMetadataCard;
